// vendor libs
const DomUtil = {};

const elementTop = (el) => el.getBoundingClientRect().top;
const getDistanceFromTop = (el) =>
  elementTop(el) + window.pageYOffset - document.documentElement.clientTop;
const isVisible = (el) =>
  el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;

/**
 * Sets the scrollTop of $scrollable such that $child (a child of $scrollable)
 * is completely in the viewable area of $scrollable.
 *
 * There are a few possible options for the implementation of this:
 *
 * 1. element.scrollIntoView
 * (https://developer.mozilla.org/en-US/docs/Web/API/element.scrollIntoView)
 *
 * This would be the nicest to use except that there is some issue where
 * scrollIntoView causes the background underneath to also
 * scroll. Similar to this issue:
 * http://stackoverflow.com/questions/11039885/scrollintoview-causing-the-whole-page-to-move
 * In theory we should be able to figure out why the scroll is getting
 * applied recursively to the background module when it's already in
 * view, perhaps by following this:
 * http://dev.w3.org/csswg/cssom-view/#scroll-an-element-into-view We
 * don't know why right now though.
 *
 * 2. (what this function does) calculating the scrollTop
 * explicitly and setting that - if the item div is above the top edge
 * of the scrollable element, aligning to the top, and if the item div
 * is below the bottom edge of the scrollable element, aligning to the
 * bottom.
 *
 * @param {!jQuery} $el
 * @param {!jQuery} $scrollable
 */
DomUtil.scrollIntoView = function($el, $scrollable) {
  // The top of the item div relative to the top
  // of it's position ancestor.  If the item is above
  // the viewable area of it's scroll container, top will be negative.
  const itemTop = $el.position().top;
  const itemBottom = itemTop + $el.outerHeight();

  const scrollHeight = $scrollable.outerHeight();
  const oldScrollTop = $scrollable.scrollTop();

  if (itemTop < 0) {
    // If the top of the selected item is above the viewport on
    // the scrollable element, align it to the top of the viewport.
    $scrollable.scrollTop(oldScrollTop + itemTop);
  } else if (itemBottom > scrollHeight) {
    // If the bottom of the selected item is below the viewport
    // on the scrollable element, align it to the bottom of the
    // viewport.
    $scrollable.scrollTop(oldScrollTop - scrollHeight + itemBottom);
  }
};

/**
 * @const
 * @type {function(function())}
 */
let raf;
if (typeof window !== 'undefined' && window.requestAnimationFrame) {
  raf = window.requestAnimationFrame;
} else {
  raf = function(callback) {
    setTimeout(callback, 16);
  };
}

/**
 * @return {!Promise.<undefined>}
 */
const requestAnimationFramePromise = function() {
  return new Promise((resolve, reject) => {
    raf(() => {
      resolve(undefined);
    });
  });
};

/**
 * Return a promise that will be resolved when writes to the DOM
 * can be done effiiently.  Use this mechanism to batch up DOM writes
 * so that they're not interleaved with DOM reads (causing layout
 * thrashing and slowness).
 */
DomUtil.deferWrite = requestAnimationFramePromise;

/**
 * DEPRECATED.  Use the promise-based DomUtil.deferWrite.
 *
 * @param {!function()} callback A function that modifies the DOM and will
 * potentially invalidate the layout or calculated styles.
 */
DomUtil.deferDomManipulation = function(callback) {
  DomUtil.deferWrite().then(callback);
};

/**
 * un-focus the currently active item
 */
DomUtil.blur = function() {
  if (document.activeElement) {
    document.activeElement.blur();
  }
};

/**
 * returns whether the body is the active element on the page
 */
DomUtil.body_is_active = function() {
  return document.activeElement.tagName === 'BODY';
};

/**
 * Adds a div the size of the viewport to the top of the page.
 * This approach enables us to check which pins were in the viewport
 * for the first page load after they're positioned even when the user may
 * have scrolled down and the viewport is at a different position.
 *
 */
DomUtil.createViewportBoundingBox = function() {
  if (this.boundingBoxRect) {
    return;
  }

  if (!this.boundingBox) {
    this.boundingBox = document.createElement('div');
  }

  this.boundingBox.className = 'pwtFirstPageBoundingBox';
  this.boundingBox.style.top = '0px';
  this.boundingBox.style.left = '0px';
  this.boundingBox.style.height = '100vh';
  this.boundingBox.style.right = '0px';
  this.boundingBox.style.position = 'absolute';
  this.boundingBox.style.zIndex = -2;
  if (document.body.prepend) {
    document.body.prepend(this.boundingBox);
  } else {
    // for IE. Based on https://developer.mozilla.org/en-US/docs/Web/API/ParentNode/prepend#Polyfill
    const docFrag = document.createDocumentFragment();
    docFrag.appendChild(this.boundingBox);
    document.body.insertBefore(docFrag, document.body.firstChild);
  }
  this.boundingBoxRect = this.boundingBox.getBoundingClientRect();
};

/**
 * Removes the bounding box when we don't need it anymore.
 */
DomUtil.destroyViewportBoundingBox = function() {
  if (!this.boundingBoxRect) {
    return;
  }
  if (this.boundingBox.parentNode) {
    this.boundingBox.parentNode.removeChild(this.boundingBox);
  }
  this.boundingBox = null;
  window.removeEventListener('resize', this.sizeBoundingBox);
};

/**
 * takes a dom element and returns true if it is active
 */
DomUtil.is_active_element = function(e) {
  return document.activeElement === e;
};

/**
 * Determine whether a part of an element is in the viewport in the vertical direction
 *
 * @param {Object} el the element to track
 * @param {number} pixelAbove the pixel above the viewport
 * @return {boolean} whether any part of the given element is visible in the viewport
 */
DomUtil.isElementInViewport = function(el, pixelAbove = 0) {
  const scrollPosition = window.pageYOffset;
  const distanceFromTop = getDistanceFromTop(el);
  const windowHeight = window.innerHeight - pixelAbove;

  return !(
    scrollPosition + windowHeight < distanceFromTop ||
    scrollPosition > distanceFromTop + el.offsetHeight
  );
};

DomUtil.isAnyPartOfElementVisibleInViewport = function(el) {
  return el && isVisible(el) && DomUtil.isElementInViewport(el);
};

/**
 * Determine whether 50 percent of an element is in the bounding box in the vertical direction or
 * if visible portion of the image is more than 20% of the bounding box.
 *
 * @param {Object} el the element to track
 * @return {boolean} whether 50% of the given element is within bounding box or
 * is more than 20% of the bounding box.
 */
DomUtil.isElementInBoundingBox = function(el) {
  if (!el || !this.boundingBoxRect) {
    return false;
  }

  const elRect = el.getBoundingClientRect();
  // get height of visible portion of element
  let elHeightInBoundingBox = 0;

  // top of the element is above bounding box and bottom of the element is in bounding box.
  if (elRect.top < this.boundingBoxRect.top && elRect.bottom < this.boundingBoxRect.bottom) {
    elHeightInBoundingBox = elRect.height - (this.boundingBoxRect.top - elRect.top);
    // top of the element is in bounding box and bottom of the element is below bounding box.
  } else if (elRect.top > this.boundingBoxRect.top && elRect.bottom > this.boundingBoxRect.bottom) {
    elHeightInBoundingBox = elRect.height - (elRect.bottom - this.boundingBoxRect.bottom);
    // top of the element is above the bounding box and bottomg of the element is below bounding box.
    // i.e middle of the element is in the bounding box.
  } else if (elRect.top < this.boundingBoxRect.top && elRect.bottom > this.boundingBoxRect.bottom) {
    elHeightInBoundingBox = this.boundingBoxRect.height;
  }

  const elHalfHeight = elRect.height / 2;
  return (
    elRect.right < this.boundingBoxRect.right &&
    // Takes care of the case that the entire element is in the viewport.
    // (if more than half of the element is not outside the box or the amount of
    // the element in the box is more than 20% of the box)
    (!(
      this.boundingBoxRect.bottom < elRect.bottom - elHalfHeight ||
      this.boundingBoxRect.top > elRect.top + elHalfHeight
    ) ||
      (elHeightInBoundingBox / this.boundingBoxRect.height) * 100 > 20)
  );
};

export default DomUtil;
